import * as statsUtil from '../util/stats';
import * as domUtils from '../util/dom';
import * as device from '../util/device';
import * as VideoConfig from '../config';
import Sizzle from 'sizzle';
import debugUtil from '../util/debug';

const Debug = debugUtil('mms-stats');

const NAMESPACE = 'mms';
const STREAM_SENSE_SCRIPT_URL = 'https://sb.scorecardresearch.com/c2/plugins/streamsense_plugin_html5.js';

const instances = new Map();

const getVideoElement = (api) => Sizzle('video', api.element())[0];

const loadStreamSenseScript = () => domUtils.getScript(STREAM_SENSE_SCRIPT_URL);

const mmsStatisticsDefaults = {
    mms_tid: '000000',
    ns_st_pr: 'unknown_program',
    ns_st_ep: 'unknown_episode',
    svt_taggar: ''
};

const buildMmsValues = (mmsStatistics) => {
    const mmsValues = {
        client: statsUtil.getClient(),
        svt_app: 'webb',
        svt_url: `${window.location.origin}${window.location.pathname}`,
        svt_contenttype: mmsStatistics.svt_contenttype,
        svt_taggar: mmsStatistics.svt_taggar
    };

    if (window.SVT_STATISTICS && window.SVT_STATISTICS.contextData) {
        const contextData = window.SVT_STATISTICS.contextData;
        Object.assign(mmsValues, {
            svt_app: contextData.svt_appname,
            svt_domain: contextData.svt_domain,
            svt_level1: contextData.svt_level1,
            svt_level2: contextData.svt_level2,
            svt_level3: contextData.svt_level3
        });
    }

    return JSON.stringify(mmsValues);
};

const buildClipData = (metadata, mmsStatistics, autoplayOfNextVideo) => {
    const clipData = {
        ns_st_pr: mmsStatistics.ns_st_pr,
        ns_st_ep: mmsStatistics.ns_st_ep,
        ns_st_cl: mmsStatistics.ns_st_cl,
        ns_st_li: (metadata.live || metadata.simulcast) ? 1 : 0,

        name: `${statsUtil.getClient()}.${mmsStatistics.ns_st_pr}.${mmsStatistics.ns_st_ep}`,
        mms_tid: mmsStatistics.mms_tid,
        mms_auto: autoplayOfNextVideo ? 'yes' : 'no'
    };

    clipData.mms_values = buildMmsValues(mmsStatistics);

    return clipData;
};

const labelsToString = (labels) => {
    const keys = Object.keys(labels);
    const pairs = keys.map((key) => `${key}=${labels[key]}`);

    return pairs.join(',');
};

const getPersistentLabelsString = () => {
    const client = statsUtil.getClient();

    return labelsToString({
        ns_site: statsUtil.getComscoreSite(),
        ns_st_mp: 'html5',
        ns_st_mv: `svtvideoplayer-${VERSION}`,
        mms_clnr: statsUtil.getMmsClientNr(),
        mms_subsite: `${client}.html5`,
        client: client
    });
};

class MmsStats {
    constructor(api) {
        this.api = api;
        this.videoElement = getVideoElement(this.api);

        // The stream sense object is loaded and initialized in enable().
        this.streamSense = null;

        // Used to store current metadata. We must store the metadata if the
        // metadata is available before the streamsense script has been loaded.
        this.metadata = null;

        // Used by enable/disable functions to toggle active state. Note that,
        // enabled does not guarantee that streamSense has been initialized.
        this.enabled = false;

        loadStreamSenseScript();

        api.filters.push({
            load: (videoId, autoplay, options) => {
                // Disable the plugin for videos loaded on chromecast, because
                // the statistics will be reported by the chromecast receiver.
                if (options.chromecast) {
                    this.disable();
                } else {
                    this.enable();
                }
            }
        });

        api.on(NAMESPACE + '.metadata', (data) => {
            this.metadata = data.detail;
            this.updateMetadata();
        });

        api.once(NAMESPACE + '.playing', () => {
            if (device.isPictureInPictureCapable(this.videoElement)) {
                this.updatePiPMode();

                domUtils.addEvents(this.videoElement, 'webkitpresentationmodechanged', () => {
                    this.updatePiPMode();
                }, NAMESPACE);
            }

            if (device.isAirplayCapable()) {
                this.updateAirplayMode();

                domUtils.addEvents(this.videoElement, 'webkitcurrentplaybacktargetiswirelesschanged', () => {
                    this.updateAirplayMode();
                }, NAMESPACE);
            }
        });
    }

    updateMetadata() {
        if (!this.streamSense || !this.metadata || !this.metadata.mmsStatistics) {
            return;
        }

        const mmsStatistics = Object.assign({}, mmsStatisticsDefaults, this.metadata.mmsStatistics);

        this.streamSense.setPlaylist({ ns_st_pl: mmsStatistics.ns_st_pl });
        this.streamSense.setClip(buildClipData(this.metadata, mmsStatistics, this.api.isAutoplayOfNextVideo()));
    }

    updatePiPMode() {
        if (!this.streamSense) {
            return;
        }

        const isPiPMode = this.videoElement.webkitPresentationMode === 'picture-in-picture';
        this.streamSense.setLabel('svt_pip', isPiPMode);
    }

    updateAirplayMode() {
        if (!this.streamSense) {
            return;
        }

        const isAirplayMode = this.videoElement.webkitCurrentPlaybackTargetIsWireless;
        this.streamSense.setLabel('svt_exscreen', isAirplayMode);
    }

    enable() {
        if (this.enabled) {
            return;
        }

        this.enabled = true;

        loadStreamSenseScript().then(() => {
            // The plugin might have been disabled while the script was loading.
            // So we must make sure the plugin is still enabled.
            if (this.enabled) {
                Debug('Creating new StreamSense object');
                this.streamSense = new ns_.StreamSense.Html5(this.videoElement, {
                    c2: statsUtil.getComscoreC2Value(),
                    logurl: statsUtil.getScorecardResearchUrl(),
                    persistentlabels: getPersistentLabelsString()
                });

                // If the metadata was loaded before the script was loaded,
                // the metadata have been stored and can be set immediately
                // onto the stream sense object.
                this.updateMetadata();

            }
        });
    }

    disable() {
        if (!this.enabled) {
            return;
        }

        this.enabled = false;

        if (this.streamSense) {
            this.streamSense = null;
        }
    }

    destroy() {
        this.api.off(NAMESPACE + '.metadata');
        this.api.off(NAMESPACE + '.playing');

        domUtils.removeEvents(this.videoElement, 'webkitpresentationmodechanged', NAMESPACE);
        domUtils.removeEvents(this.videoElement, 'webkitcurrentplaybacktargetiswirelesschanged', NAMESPACE);

        this.disable();
    }
}

export { NAMESPACE as namespace };

export function create(api) {
    if (instances.has(api)) {
        Debug.warn(
            'mms-stats was already created for this api instance and has' +
            ' not been destroyed. The previous instance may leak memory and' +
            ' listeners.'
        );
    }

    const instance = new MmsStats(api);
    instances.set(api, instance);

    return instance;
}

export function destroy(api) {
    if (instances.has(api)) {
        instances.get(api).destroy();
        instances.delete(api);
    }
}




// WEBPACK FOOTER //
// ./src/js/plugins/mms-stats.js